import { useState } from 'react';

import { useMsal } from "@azure/msal-react";

import { Item, Solution } from '../../api-client';
import { IHookCallback } from '../models/IHookCallback';
import { SolutionApiClient } from '../../providers/api-provider';
import { NotificationHelper } from '../../utils/notification';
import { useTranslation } from 'react-i18next';

export const useSolution = () => {
    const {
        instance
    } = useMsal();

    const {
        t: translate
    } = useTranslation();

    const [solutions, setSolutions] = useState<Solution[]>();
    const [solutionsLoading, setSolutionsLoading] = useState<boolean>(false);

    const handleSolutions = async () => {
        setSolutionsLoading(true);

        const clientApi = await SolutionApiClient(instance);

        clientApi.getSolutions()
            .then((response) => {
                setSolutions(response.data);
            })
            .catch((reason) => {
                setSolutions([]);

                NotificationHelper.createNotification({
                    type: 'error',
                    message: translate('system.notification.getSolutions.title'),
                    title: translate('system.notification.getSolutions.message')
                });
            })
            .finally(() => {
                setSolutionsLoading(false);
            });
    }

    // CRUD Operations

    interface ICreateSolution extends IHookCallback {
        solution: Solution;
    }

    const [createSolution, setCreateSolution] = useState<Item>();
    const [createSolutionLoading, setCreateSolutionLoading] = useState<boolean>(false);

    const handleCreateSolution = async (params: ICreateSolution) => {
        setCreateSolutionLoading(true);

        const clientApi = await SolutionApiClient(instance);

        clientApi.insertSolution(params.solution)
            .then((response) => {
                setCreateSolution(response.data);

                params.onSuccess && params.onSuccess();

                NotificationHelper.createNotification({
                    type: 'success',
                    message: translate('system.notification.insertSolutionSuccess.title'),
                    title: translate('system.notification.insertSolutionSuccess.message')
                });
            })
            .catch((reason) => {
                setCreateSolution(undefined);

                params.onError && params.onError();

                NotificationHelper.createNotification({
                    type: 'error',
                    message: translate('system.notification.insertSolution.title'),
                    title: translate('system.notification.insertSolution.message')
                });
            })
            .finally(() => {
                setCreateSolutionLoading(false);

                params.onAlways && params.onAlways();
            });
    }

    interface IRetrieveSolution extends IHookCallback {
        id: string;
        onRetrieveSuccess: (solution: Solution) => void;
    }

    const [retrieveSolution, setRetrieveSolution] = useState<Solution>();
    const [retrieveSolutionLoading, setRetrieveSolutionLoading] = useState<boolean>(false);

    const handleRetrieveSolution = async (params: IRetrieveSolution) => {
        setRetrieveSolutionLoading(true);

        const clientApi = await SolutionApiClient(instance);

        clientApi.getSolution(params.id)
            .then((response: any) => {
                setRetrieveSolution(response.data);

                params.onRetrieveSuccess(response.data);
            })
            .catch((reason) => {
                params.onError && params.onError();

                NotificationHelper.createNotification({
                    type: 'error',
                    message: translate('system.notification.getSolution.title'),
                    title: translate('system.notification.getSolution.message')
                });
            })
            .finally(() => {
                setRetrieveSolutionLoading(false);
                
                params.onAlways && params.onAlways();
            });
    }

    interface IUpdateSolution extends IHookCallback {
        id: string;
        solution: Solution;
    }

    const [updateSolutionLoading, setUpdateSolutionLoading] = useState<boolean>(false);

    const handleUpdateSolution = async (params: IUpdateSolution) => {
        setUpdateSolutionLoading(true);

        const clientApi = await SolutionApiClient(instance);

        clientApi.updateSolution(params.id, params.solution)
            .then((response) => {
                params.onSuccess && params.onSuccess();

                NotificationHelper.createNotification({
                    type: 'success',
                    message: translate('system.notification.updateSolutionSuccess.title'),
                    title: translate('system.notification.updateSolutionSuccess.message')
                });
            })
            .catch((reason) => {
                params.onError && params.onError();

                NotificationHelper.createNotification({
                    type: 'error',
                    message: translate('system.notification.updateSolution.title'),
                    title: translate('system.notification.updateSolution.message')
                });
            })
            .finally(() => {
                setUpdateSolutionLoading(false);

                params.onAlways && params.onAlways();
            });
    }

    interface IDeleteSolution extends IHookCallback {
        id: string;
    }

    const [deleteSolutionLoading, setDeleteSolutionLoading] = useState<boolean>(false);

    const handleDeleteSolution = async (params: IDeleteSolution) => {
        setDeleteSolutionLoading(true);

        const clientApi = await SolutionApiClient(instance);

        clientApi.deleteSolution(params.id)
            .then((response) => {
                params.onSuccess && params.onSuccess();

                NotificationHelper.createNotification({
                    type: 'success',
                    message: translate('system.notification.deleteSolutionSuccess.title'),
                    title: translate('system.notification.deleteSolutionSuccess.message')
                });
            })
            .catch((reason) => {
                params.onError && params.onError();

                NotificationHelper.createNotification({
                    type: 'error',
                    message: translate('system.notification.deleteSolution.title'),
                    title: translate('system.notification.deleteSolution.message')
                });
            })
            .finally(() => {
                setDeleteSolutionLoading(false);

                params.onAlways && params.onAlways();
            });
    }

    return {
        handleSolutions,
        solutions,
        solutionsLoading,
        // CRUD Operations
        handleCreateSolution,
        createSolution,
        createSolutionLoading,
        handleRetrieveSolution,
        retrieveSolution,
        retrieveSolutionLoading,
        handleUpdateSolution,
        updateSolutionLoading,
        handleDeleteSolution,
        deleteSolutionLoading
    }
}